// components/OfflineToast.tsx
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { WifiOff, X } from "lucide-react";
import toast from "react-hot-toast";
import { RootState } from "@/redux/store";
import { setOfflineStatus } from "@/redux/authSlice";

export default function OfflineToast() {
  const { isOffline } = useSelector((state: RootState) => state.auth);
  const [toastId, setToastId] = useState<string | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const handleOnline = () => dispatch(setOfflineStatus(false));
    const handleOffline = () => dispatch(setOfflineStatus(true));

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [dispatch]);

  useEffect(() => {
    if (isOffline) {
      const id = toast.custom(
        (t) => (
          <div
            className={`z-50 flex items-center gap-3 rounded-2xl border p-4 shadow-lg backdrop-blur-lg transition-all duration-300 ${
              t.visible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            } 
            ${
              document.documentElement.classList.contains("dark")
                ? "bg-slate-800/70 border-slate-700"
                : "bg-white/70 border-gray-200"
            }`}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <WifiOff className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 dark:text-white">
                You’re Offline
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Some features may be unavailable.
              </p>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="rounded-lg p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        ),
        {
          id: "offline-toast",
          duration: Infinity,
          position: "bottom-right",
        },
      );

      setToastId(id);
    } else {
      if (toastId) {
        toast.dismiss(toastId);
        toast.dismiss("offline-toast");
      }
    }
  }, [isOffline, toastId]);

  return null;
}
